/**
 * Dependencies.
 */
import {
  getModule,
  Module,
  Mutation,
  VuexModule
} from 'vuex-module-decorators';

import CollectModel from '@/api/collect/collect.model';
import DeliveryModel from '@/api/delivery/delivery.model';
import store from '@/plugins/store.plugin';

/**
 * Main module.
 */
@Module({
  dynamic: true,
  name: 'main',
  namespaced: true,
  store
})
class Main extends VuexModule {
  /**
   * Property `collect`.
   */
  private collect: CollectModel = null;

  /**
   * Property `delivery`.
   */
  private delivery: DeliveryModel = null;

  /**
   * Property `orderType`.
   */
  private orderType: 'DELIVERY' | 'COLLECT' = 'DELIVERY';

  /**
   * Property `distributionCenterCode`.
   */
  distributionCenterCode = null;

  /**
   * Property `orderProcessDelayed`.
   */
  orderProcessDelayed = false;

  /**
   * Getter `isDelivery`.
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter `order`.
   */
  get order() {
    return this.isDelivery ? { ...this.delivery } : { ...this.collect };
  }

  /**
   * Getter `countryCode`.
   */
  get countryCode() {
    return this.order?.address?.countryCode;
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    return this.isDelivery
      ? this.delivery?.orderCode
      : this.collect?.order?.code;
  }

  /**
   * Mutation `setCollectData`.
   */
  @Mutation
  setCollectData(data: CollectModel) {
    this.collect = data;
  }

  /**
   * Mutation `setDeliveryData`.
   */
  @Mutation
  setDeliveryData(data: DeliveryModel) {
    this.delivery = data;
  }

  /**
   * Mutation `setOrderType`.
   */
  @Mutation
  setOrderType(type: 'DELIVERY' | 'COLLECT') {
    this.orderType = type;
  }

  /**
   * Mutation `setDistributionCenterCode`.
   */
  @Mutation
  setDistributionCenterCode(code: string) {
    this.distributionCenterCode = code;
  }

  /**
   * Mutation `setOrderProcessDelayed`.
   */
  @Mutation
  setOrderProcessDelayed(orderProcessDelayed: boolean) {
    this.orderProcessDelayed = orderProcessDelayed;
  }
}

/**
 * Export `mainStore`.
 */
export const mainStore = getModule(Main);
